/**
 * The typed HTTP client. Every request in the app goes through here.
 *
 * Shapes come from `api-types.ts`, which is generated from the API's OpenAPI
 * document — a renamed field is a compile error, not a runtime `undefined`.
 */
import createClient, { type Middleware } from 'openapi-fetch'
import type { components, paths } from './api-types'

/** RFC 9457 problem document. Every 4xx/5xx from this API is one of these. */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  /** Present on validation failures: field name -> messages. */
  errors?: Record<string, string[]>
  [key: string]: unknown
}

/**
 * Thrown by {@link unwrap} for any non-2xx response, and for network failures
 * (which carry `status: 0`). Screens should render `error.message` — it is
 * already the most specific sentence we have.
 */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails | null
  readonly url: string

  constructor(status: number, problem: ProblemDetails | null, url: string) {
    super(
      problem?.detail ??
        problem?.title ??
        (status === 0
          ? 'Could not reach the server. Check your connection and try again.'
          : `Request failed (${status}).`),
    )
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.url = url
  }

  /** Validation messages keyed by field, or null when this is not a 400. */
  get fieldErrors(): Record<string, string[]> | null {
    return this.problem?.errors ?? null
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

/**
 * Where the API lives. Empty in dev and in production alike: Vite proxies
 * `/api` to the backend locally, and in production the API serves the SPA from
 * the same origin, so the session cookie is always first-party.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? ''

/** Paths whose 401 is an answer, not a session expiry. */
const AUTH_PATHS = ['/api/auth/login', '/api/auth/me', '/api/auth/logout']

type UnauthorizedHandler = () => void

let onUnauthorized: UnauthorizedHandler = () => {
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/login') return
  const next = window.location.pathname + window.location.search
  window.location.assign(`/login?next=${encodeURIComponent(next)}`)
}

/**
 * Lets the router take over session-expiry redirects so we get a client-side
 * navigation instead of a full page reload. `App` wires this up on mount.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler
}

/**
 * A 401 from anywhere except the auth endpoints means the cookie expired
 * mid-session. Bounce to /login once, remembering where we were.
 */
const sessionMiddleware: Middleware = {
  onResponse({ request, response }) {
    if (response.status === 401) {
      const path = new URL(request.url).pathname
      if (!AUTH_PATHS.includes(path)) onUnauthorized()
    }
    return undefined
  },
}

export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  // The session is an HttpOnly cookie (there is no token to attach by hand).
  credentials: 'include',
})

api.use(sessionMiddleware)

/**
 * Turns openapi-fetch's `{ data, error }` result into a value-or-throw, which
 * is what TanStack Query wants:
 *
 * ```ts
 * queryFn: ({ signal }) => unwrap(api.GET('/api/accounts', { signal }))
 * ```
 */
export async function unwrap<TData>(
  result:
    | Promise<{ data?: TData; error?: unknown; response: Response }>
    | { data?: TData; error?: unknown; response: Response },
): Promise<TData> {
  let settled: { data?: TData; error?: unknown; response: Response }
  try {
    settled = await result
  } catch (cause) {
    // Network-level failure: openapi-fetch rethrows fetch's TypeError.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, null, 'unknown')
  }

  const { data, error, response } = settled
  if (response.ok) return data as TData

  throw new ApiError(response.status, asProblem(error), response.url)
}

function asProblem(error: unknown): ProblemDetails | null {
  return error !== null && typeof error === 'object' ? (error as ProblemDetails) : null
}

/**
 * Raw fetch against the API, same credentials and same error type. Needed for
 * the two things the typed client cannot express: the auth endpoints (their
 * 200 bodies are absent from the OpenAPI document) and the AI chat SSE stream.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      credentials: 'include',
      ...init,
      headers: { Accept: 'application/json', ...init.headers },
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, null, path)
  }

  if (!response.ok) {
    if (response.status === 401 && !AUTH_PATHS.includes(path)) onUnauthorized()
    throw new ApiError(response.status, await readProblem(response), path)
  }

  return response
}

async function readProblem(response: Response): Promise<ProblemDetails | null> {
  try {
    return (await response.json()) as ProblemDetails
  } catch {
    return null
  }
}

/**
 * The session document. Hand-written because ASP.NET does not describe the
 * 200 body of `/api/auth/*` in its OpenAPI output; it mirrors
 * `PersonalFinance.Api.Endpoints.SessionResponse` exactly.
 */
export interface SessionResponse {
  authenticated: boolean
  username?: string | null
  displayName?: string | null
  /** 'Owner' | 'Member' | 'ReadOnly' | 'root' */
  role?: string | null
  /** Root administers users and can read no financial data at all. */
  isRoot?: boolean
  userId?: string | null
  memberId?: string | null
  mustChangePassword?: boolean
  /** True when no accounts exist yet, so the UI can explain first-run. */
  setupRequired?: boolean
  signedInAt?: string | null
}

export async function fetchSession(signal?: AbortSignal): Promise<SessionResponse> {
  // /api/auth/me answers 200 with `authenticated: false` rather than 401, so an
  // anonymous visitor never trips the session-expiry redirect.
  const response = await apiFetch('/api/auth/me', { signal })
  return (await response.json()) as SessionResponse
}

export async function login(username: string, password: string): Promise<SessionResponse> {
  const response = await apiFetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  return (await response.json()) as SessionResponse
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiFetch('/api/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  })
}

export async function logout(): Promise<void> {
  await apiFetch('/api/auth/logout', { method: 'POST' })
}

/** Shorthand for `components['schemas'][K]` — the whole app's data vocabulary. */
export type Schema<K extends keyof components['schemas']> = components['schemas'][K]
